import FetchModelAssignments from '@Application/Ai/AiConfiguration/Assignments/Queries/FetchModelAssignments';
import fetchModelAssignments from '@Application/Ai/AiConfiguration/Assignments/fetchModelAssignments';
import FetchAiModels from '@Application/Ai/AiConfiguration/Models/Queries/FetchAiModels';
import fetchAiModels from '@Application/Ai/AiConfiguration/Models/fetchAiModels';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import UsageAssignmentsBoard from '@UI/Pages/AiAssignments/Shared/UsageAssignmentsBoard';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import { Bot } from 'lucide-react';
import type * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Skeleton } from '@verclam/ui-kit';

const catalogSkeletonStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

const cardsSkeletonGridStyle = css`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(min(18rem, 100%), 1fr));
	gap: 1.25rem;
`;

/**
 * Owns the two queries this screen needs — the model catalogue, then the account assignment slot —
 * and their loading/error states, the same two-step chaining `AiEnginesContent` used for the same
 * data. Nothing above the account level to gate on here: the route itself already requires
 * `AI_ACCOUNT_ASSIGNMENT_READ`.
 */
const AccountAiAssignmentsContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;

	const {
		data: models,
		error: modelsError,
		refetch: refetchModels,
	} = useStoreQuery({
		selector: (state) => state.aiConfigurationStore.models,
		stopOn: (value) => !!value && value.scopeId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAiModels(new FetchAiModels(accountId)));
		},
		deps: [accountId],
	});

	const {
		data: assignments,
		error: assignmentsError,
		refetch: refetchAssignments,
	} = useStoreQuery({
		selector: (state) => state.aiConfigurationStore.accountAssignments,
		stopOn: (value) => !!value && value.scopeType === 'account' && value.scopeId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchModelAssignments(new FetchModelAssignments({ type: 'account', accountId })));
		},
		deps: [accountId],
	});

	if (accountId === null) {
		return (
			<EmptyState
				title="Aucun compte"
				description="Vous n’êtes rattaché à aucun compte : les affectations ne peuvent pas être configurées."
				icon={<Bot size={28} />}
			/>
		);
	}

	if (models === null && modelsError === null) {
		return (
			<div css={catalogSkeletonStyle}>
				<Skeleton height="16rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	if (models === null) {
		return <ErrorState title="Impossible de charger les modèles du compte." onRetry={refetchModels} />;
	}

	if (assignments === null && assignmentsError === null) {
		return (
			<div css={cardsSkeletonGridStyle}>
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
				<Skeleton height="10rem" borderRadius={theme.radii.xlarge} animation="wave" />
			</div>
		);
	}

	if (assignments === null) {
		return (
			<ErrorState title="Impossible de charger les modèles du compte." onRetry={refetchAssignments} />
		);
	}

	return (
		<UsageAssignmentsBoard
			scope={{ type: 'account', accountId }}
			assignments={assignments}
			models={models}
		/>
	);
};

export default AccountAiAssignmentsContent;
